'use client'

import { RouteModel } from '@polaris/core/models'
import type { Route } from '@polaris/core/services'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useNavigationHistory } from '../navigation/actions'
import {
  amplitudeEventTracingAtom,
  errorAtom,
  isSuccessAtom,
  pageTitleEffectAtom,
  routeAtom,
  routeEventTracingAtom,
  routeMachineAtom,
  routeMachineSnapshotAtom,
  routeUpdateEffectAtom,
  walletKeyringUpdateEffectAtom,
} from './atoms'

interface UseRouteMachineActionsProps {
  route: Route
}

/**
 * Subscribe to side effects related to route state machine
 *
 * NOTE: This should be called once for example inside layout.tsx or root component
 */
export const useRouteMachineSubscriptions = () => {
  useAtom(walletKeyringUpdateEffectAtom)
  useAtom(routeUpdateEffectAtom)
  useAtom(amplitudeEventTracingAtom)
  useAtom(routeEventTracingAtom)
  useAtom(pageTitleEffectAtom)
}

interface UseTradeProps {
  autoConfirm?: boolean
}

export const useTrade = (props: UseTradeProps = { autoConfirm: true }) => {
  const { autoConfirm } = props
  const [routeModel, setRouteModel] = useAtom(routeAtom)
  const actorRef = useAtomValue(routeMachineAtom)
  const state = useAtomValue(routeMachineSnapshotAtom)
  const isSuccess = useAtomValue(isSuccessAtom)
  const error = useAtomValue(errorAtom)
  const { navigateToPreviousPage } = useNavigationHistory()

  const start = useCallback(
    ({ route }: UseRouteMachineActionsProps) => {
      if (routeModel?.ID !== route.ID) {
        setRouteModel(new RouteModel(route))
      }

      /**
       * We need to start it manually only when we actual use this hooks,
       * this avoid to randomly start the state machine if we don't have a valid route
       */
      if (
        state.value === 'Initializing' &&
        route &&
        route.ID !== state.context.route?.ID
      ) {
        actorRef.start()
        actorRef.send({
          type: 'start',
          input: new RouteModel(route),
        })

        if (autoConfirm) {
          actorRef.send({ type: 'confirm' })
        }
      }
    },
    [
      actorRef,
      autoConfirm,
      routeModel?.ID,
      setRouteModel,
      state.context.route?.ID,
      state.value,
    ],
  )

  /**
   * Clears the route model from the swap tool and stops the route machine
   * This results in the swap tool being emptied allowing for the new quotes
   * to be shown. However, the route may progress in the background.
   */
  const clearRoute = useCallback(() => {
    setRouteModel(undefined)
    actorRef.send({ type: 'stop' })
  }, [actorRef, setRouteModel])

  /** Navigate back and clear route if route is successful or has an error */
  const goBack = useCallback(() => {
    navigateToPreviousPage()

    if (isSuccess || error) {
      clearRoute()
    }
  }, [navigateToPreviousPage, isSuccess, error, clearRoute])

  return {
    start,
    clearRoute,
    goBack,
  }
}

/**
 * This must be used once for state machine hydration
 */
export const useRouteMachine = ({ route }: UseRouteMachineActionsProps) => {
  const actorRef = useAtomValue(routeMachineAtom)
  const state = useAtomValue(routeMachineSnapshotAtom)
  const { start } = useTrade({ autoConfirm: false })
  const setRouteAtom = useSetAtom(routeAtom)

  /**
   * This will start once when we hydrate the route page
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omitting dependencies to avoid infinite re-renders
  useEffect(() => {
    setRouteAtom(new RouteModel(route))
    start({ route })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    state,
    actorRef,
  }
}
